import { Injectable } from '@angular/core';
import { UserControllerService } from '@app/generated/service';
import { UserDto } from '@app/generated/model';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ResourceType } from '@app/shared/enums/resource-type';
import { env } from '../../../environments/env';
import { User } from '@admin/models/user';
import { BaseService, Resource } from '@app/core/services/base.service';
import { UserDetails } from '@admin/models/user-details';

@Injectable({
  providedIn: 'root',
})
export class UserService extends BaseService {
  private user$: BehaviorSubject<UserDto> = new BehaviorSubject(null);

  constructor(
    private userController: UserControllerService,
    readonly http: HttpClient,
  ) {
    super(http);
  }

  @Resource(ResourceType.UserGetCurrentUser)
  public getCurrentUser(): Observable<UserDto> {
    return this.userController.getUserInfo();
  }

  async load(): Promise<void> {
    const user = await this.userController.getUserInfo().toPromise();
    this.user$.next(user);
  }

  get(): UserDto {
    return this.user$.value;
  }

  getObserver(): Observable<UserDto> {
    return this.user$;
  }

  async setProfile(siteIds: Array<string>, workCenterIds: Array<string>, projectIds: Array<string>): Promise<void> {
    await this.userController.setProfile({ siteIds, workCenterIds, projectIds }).toPromise();
    await this.load();
  }

  @Resource(ResourceType.LoadAllUsers)
  loadAllUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${env.get().api.url}/admin/users`);
  }

  @Resource(ResourceType.AddUser)
  addUser(user: User): Observable<User> {
    return this.http.post<User>(`${env.get().api.url}/admin/users`, user);
  }

  @Resource(ResourceType.LoadUserDetails)
  loadUserDetails(username: string): Observable<UserDetails> {
    return this.http.get<UserDetails>(`${env.get().api.url}/admin/users/${username}`);
  }

  @Resource(ResourceType.SaveUser)
  saveUser(user: User): Observable<User> {
    return this.http.put<User>(`${env.get().api.url}/admin/users/${user.username}`, user);
  }

  updateLastSeenVersion(version: string): Observable<any> {
    return this.userController.updateLastSeenVersion(version);
  }

  filterUsers(users: User[], filter: string): User[] {
    if (!filter) return users;
    const search = filter.trim().toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(search) ||
        user.fullName?.toLowerCase().includes(search) ||
        user.sapId?.toLowerCase().includes(search),
    );
  }
}
